/* =============================================================================
 * Delta Clothing — facet disclosure pop-out controller
 *
 * Three UX rules layered on top of the vanilla Stockmart `<details>`-based
 * facet filters (which native HTML doesn't give us for free):
 *
 *   1. Only one filter open at a time. Opening AVAILABILITY auto-closes
 *      PRICE / SIZE / MORE FILTERS, etc. Matches the menu-bar metaphor the
 *      horizontal layout already implies.
 *   2. Pressing Escape anywhere on the page closes whichever filter is
 *      open. (Vanilla Stockmart only listens on the wrapper, which means
 *      Escape works only if focus is inside the filter — not after a stray
 *      click on the product grid.)
 *   3. Clicking outside the open filter closes it. The pop-out panel is
 *      `position: absolute` (see b2b-collection-layout.css) so the grid
 *      below doesn't shift; this listener gives the customer the same
 *      "click anywhere to dismiss" gesture they expect from a real
 *      dropdown menu.
 *
 * Scope is intentionally desktop-only: mobile uses Stockmart's slide-out
 * facets panel which already handles outside-tap + back-button close.
 *
 * Loaded after facets.js so its toggle listeners are already wired.
 * Re-binds on every Filament XHR-style page render so it survives
 * `FacetFiltersForm.renderSectionFromFetch` repainting the wrapper.
 * =============================================================================*/
(function () {
  if (typeof document === 'undefined') return;

  // We're only concerned with the desktop facets wrapper (#FacetsWrapperDesktop).
  // Stockmart renders a separate mobile drawer that should keep vanilla
  // behaviour, so the selector pins us to the desktop tree.
  var WRAPPER_SEL = '#FacetsWrapperDesktop';
  var DISCLOSURE_SEL = WRAPPER_SEL + ' .facets__disclosure';

  function getDisclosures() {
    return Array.prototype.slice.call(document.querySelectorAll(DISCLOSURE_SEL));
  }

  function closeAll(except) {
    getDisclosures().forEach(function (d) {
      if (d !== except && d.hasAttribute('open')) d.removeAttribute('open');
    });
  }

  function onToggle(e) {
    var d = e.target;
    if (!d || d.tagName !== 'DETAILS') return;
    if (!d.matches(DISCLOSURE_SEL)) return;
    if (d.open) closeAll(d);
  }

  function onKey(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var openOne = document.querySelector(DISCLOSURE_SEL + '[open]');
    if (!openOne) return;
    openOne.removeAttribute('open');
    // Return focus to the summary so keyboard users keep a logical
    // landing spot instead of falling back to <body>.
    var summary = openOne.querySelector('summary');
    if (summary) summary.focus();
  }

  function onDocClick(e) {
    var openOnes = document.querySelectorAll(DISCLOSURE_SEL + '[open]');
    if (!openOnes.length) return;
    Array.prototype.forEach.call(openOnes, function (d) {
      // Click inside the same disclosure (summary toggles itself, panel
      // contents are selectable) is a no-op. We only react when the
      // click is completely outside the open disclosure.
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  }

  // `toggle` doesn't bubble, so we install per-element listeners. The
  // delegated keydown + document click cover the cross-element rules.
  function bind() {
    getDisclosures().forEach(function (d) {
      if (d.dataset.b2bPopoutBound === '1') return;
      d.dataset.b2bPopoutBound = '1';
      d.addEventListener('toggle', onToggle);
    });
  }

  document.addEventListener('keydown', onKey);
  document.addEventListener('click', onDocClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }

  // Vanilla Stockmart's FacetFiltersForm re-renders the wrapper after
  // a filter change (it swaps the innerHTML of #FacetFiltersForm with
  // a freshly fetched section). The new <details> nodes have no toggle
  // listener yet, so a MutationObserver re-binds whenever the wrapper
  // gets replaced. Cheap because we only react to subtree mutations
  // inside the facets form.
  var form = document.getElementById('FacetFiltersForm');
  if (form && window.MutationObserver) {
    new MutationObserver(bind).observe(form, { childList: true, subtree: true });
  }
})();
